import { Box, Button, Grid, Typography, useTheme } from '@mui/material';
import { useProcoreIntegration } from 'app/hooks/erp-integration/useProcoreIntegration';

import { useAgaveIntegration } from 'app/hooks/erp-integration/useAgaveIntegration';
import ConnectButton from 'app/shared-components/connect-button/ConnectButton';
import ContainerComponent from 'app/shared-components/container-component/container-component';
import ConnectToYourAccountingSystem from 'app/shared-components/erp-connection/ConnectToYourAccountingSystem';
import AcumaticaConnectModal from 'app/pages/settings/erp-connection/AcumaticaConnectModal';
import Sage300ConnectModal from 'app/pages/settings/erp-connection/Sage300ConnectModal';
import SpectrumConnectionModal from 'app/pages/settings/erp-connection/SpectrumConnectionModal';
import VistaConnectionModal from 'app/pages/settings/erp-connection/VistaConnectionModal';
import QBDConnectionModal from 'app/pages/settings/erp-connection/QBDConnectionModal';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import VerticalStyledToggleButtonGroup from 'app/shared-components/toggle-button-group/VerticalStyledToggleButtonGroup';
import { ERPSpeedchainOptions, AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import {
  createSpectrumConnection,
  createVistaConnection,
  fetchQbwcConnection,
  getErpConnection,
  getIntacctAuthUrl,
  getQboAuthUrl,
  SpectrumConnectionResult,
  VistaConnectionResult,
} from 'app/services/erp-connection.service';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

export interface ConnectToAccountingSystemFormProps {
  handleNext?: () => void;
}

function ConnectToAccountingSystemForm({ handleNext }: ConnectToAccountingSystemFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState<string | undefined>();
  const [showSetupMessage, setShowSetupMessage] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [spectrumModalOpen, setSpectrumModalOpen] = useState(false);
  const [spectrumCredentials, setSpectrumCredentials] = useState<SpectrumConnectionResult | null>(null);
  const [vistaModalOpen, setVistaModalOpen] = useState(false);
  const [vistaCredentials, setVistaCredentials] = useState<VistaConnectionResult | null>(null);
  const [sage300ModalOpen, setSage300ModalOpen] = useState(false);
  const [acumaticaModalOpen, setAcumaticaModalOpen] = useState(false);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  // Actual status of the referenced ERPConnection (ACTIVE/INACTIVE/ERROR).
  // null while not-yet-fetched / no connection.
  const [erpConnectionStatus, setErpConnectionStatus] = useState<string | null>(null);
  const isErpSpeedchain = useMemo(
    () => selectedPaidol?.erpSpeedchain ?? false,
    [selectedPaidol?.erpSpeedchain]
  );

  // SEE-1042 — QBD direct connect, mirrored from the Settings > Connections page
  // (ERPConnectionPage). Feature-flagged off by default; when off, QBD falls
  // through to the "not yet available" setup message like any un-built ERP.
  const { 'erpMigration.qbdDirectEnabled': qbdFlagRaw } = useIsEnabled([
    { featureName: 'erpMigration', flagName: 'qbdDirectEnabled' },
  ]);
  const qbdFlagEnabled = qbdFlagRaw === true;
  const [qbdModalOpen, setQbdModalOpen] = useState(false);
  // The modal fires onConnected only for the already-exists case; the happy path
  // ends at the verified Finish -> onClose. Both route here: re-read the QbwcConnection
  // and, once one exists, reload so the stepper resumes with fresh paidol data.
  const qbdReloadingRef = useRef(false);
  const finishQbd = useCallback(async () => {
    setQbdModalOpen(false);
    if (qbdReloadingRef.current || !selectedPaidol?.id) return;
    // Set the guard synchronously, before the await below — the "already
    // exists" path in QBDConnectionModal calls onConnected() then onClose()
    // back-to-back, both wired to finishQbd. Setting the ref only after the
    // await let both calls read it as false and both reload, racing each
    // other. Everything up to the first await runs synchronously, so setting
    // it here serializes the second call against the first.
    qbdReloadingRef.current = true;
    const status = await fetchQbwcConnection(selectedPaidol.id).catch(() => null);
    if (status) {
      // The paidol cached in Redux predates this connection — its
      // erpConnectionId/agaveSourceSystemSlug are stale, which silently hides
      // the Subjobs column and the sidesheet's coding/review UI (both key off
      // those fields). Reload so the app re-fetches a fresh paidol, exactly
      // like every other direct-ERP connect flow (Spectrum/Vista/Sage300/QBO
      // all reload on connect) — the existing erpV1Connected effect then
      // advances the stepper automatically once the fresh data loads.
      window.location.reload();
    } else {
      // No connection yet (fetch failed, or genuinely doesn't exist) — allow a
      // later finishQbd call (e.g. a real subsequent connect) to retry.
      qbdReloadingRef.current = false;
    }
  }, [selectedPaidol?.id]);

  const {
    loading: loadingAgave,
    connected: agaveConnected,
    openPopup: openAgavePopup,
  } = useAgaveIntegration({ sourceSystem: value });

  const { connected: procoreConnected, loading: loadingProcore, procoreUrl } = useProcoreIntegration();

  const erpConnectionId = selectedPaidol?.erpConnectionId;

  // A paidol can keep a stale erpConnectionId pointer after its connection is
  // deactivated or its token refresh chain breaks (INACTIVE/ERROR). Showing
  // "Connected" off the pointer alone is misleading — the sync task requires an
  // ACTIVE connection. Fetch the real status and only treat ACTIVE as connected.
  useEffect(() => {
    if (!isErpSpeedchain || !erpConnectionId) {
      setErpConnectionStatus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = await getErpConnection(erpConnectionId);
        if (!cancelled) setErpConnectionStatus(info?.connectionStatus ?? null);
      } catch (error) {
        console.error('Failed to fetch ERP connection status:', error);
        if (!cancelled) setErpConnectionStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isErpSpeedchain, erpConnectionId]);

  const erpV1Connected = useMemo(
    () => isErpSpeedchain && !!erpConnectionId && erpConnectionStatus === 'ACTIVE',
    [isErpSpeedchain, erpConnectionId, erpConnectionStatus]
  );
  // useProcoreIntegration's `connected` reads tokens from a browser-global
  // localStorage key, not anything scoped to this paidol — any paidol that has
  // ever connected Procore on this browser leaves it set, which would
  // otherwise auto-advance every OTHER paidol's stepper (QBD, Agave, etc.) the
  // instant this form mounts. Only trust it for a paidol that actually claims
  // to be a Procore client.
  const isConnected = useMemo(
    () => Boolean(selectedPaidol?.isProcoreClient && procoreConnected) || agaveConnected || erpV1Connected,
    [selectedPaidol?.isProcoreClient, procoreConnected, agaveConnected, erpV1Connected]
  );
  const isLoading = useMemo(() => loadingProcore || loadingAgave, [loadingProcore, loadingAgave]);

  useEffect(() => {
    isConnected && handleNext?.();
  }, [isConnected, handleNext]);

  // ERPv1 paidols get the Speedchain ERP connection flow
  if (isErpSpeedchain) {
    return (
      <Grid
        container
        justifyContent="center"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          pt: 3,
        }}
      >
        <Grid item xs={12}>
          <ContainerComponent
            title={t('connectAccountingSystem.title')}
            description={t('connectAccountingSystem.description')}
          />
        </Grid>
        <Grid
          item
          xs={4}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {!showSetupMessage ? (
            <VerticalStyledToggleButtonGroup
              options={ERPSpeedchainOptions}
              value={value}
              onChange={(_: React.MouseEvent<HTMLElement>, v: string) => {
                setValue(v);
                setShowSetupMessage(false);
              }}
              fullWidth
            />
          ) : (
            <Box
              sx={{
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 2,
                padding: 3,
                width: '100%',
              }}
            >
              <Box display="flex" alignItems="flex-start" gap={1.5}>
                <InfoOutlinedIcon sx={{ color: theme.palette.info.main, fontSize: 24, mt: 0.25 }} />
                <Box>
                  <Typography fontSize={16} fontWeight={600} color={theme.palette.primary.main}>
                    {value && (ERPSpeedchainOptions[value] ?? value)}
                  </Typography>
                  <Typography fontSize={14} color={theme.palette.text.secondary} mt={1}>
                    {t('erpConnection.notYetAvailable')}
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ mt: 2, p: 0 }}
                    onClick={() => setShowSetupMessage(false)}
                  >
                    {t('erpConnection.backToSelection')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
              gap: '4rem',
              flexWrap: 'wrap',
              paddingTop: 3,
            }}
          >
            <Button onClick={handleNext} size="small">
              {t('illDoThisLater')}
            </Button>
            {!showSetupMessage && (
              <ConnectButton
                loading={connecting}
                disabled={erpV1Connected || !value || connecting}
                onClick={async () => {
                  if (!selectedPaidol?.id) return;
                  if (value === AgaveSourceSystem.QUICKBOOKS_ONLINE) {
                    setConnecting(true);
                    try {
                      const authUrl = await getQboAuthUrl(selectedPaidol.id);
                      window.location.href = authUrl;
                    } catch (error) {
                      console.error('Failed to get QBO auth URL:', error);
                      enqueueSnackbar(
                        t('erpConnection.qboConnectionFailed', 'Failed to connect to QuickBooks Online'),
                        { variant: 'error' }
                      );
                      setConnecting(false);
                    }
                    return;
                  }
                  if (value === AgaveSourceSystem.SPECTRUM) {
                    setConnecting(true);
                    try {
                      const credentials = await createSpectrumConnection(selectedPaidol.id);
                      setSpectrumCredentials(credentials);
                      setSpectrumModalOpen(true);
                    } catch (error) {
                      console.error('Failed to create Spectrum connection:', error);
                      enqueueSnackbar(
                        t('erpConnection.spectrumConnectionFailed', 'Failed to create Spectrum connection'),
                        { variant: 'error' }
                      );
                    } finally {
                      setConnecting(false);
                    }
                    return;
                  }
                  if (value === AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED) {
                    setConnecting(true);
                    try {
                      const credentials = await createVistaConnection(selectedPaidol.id);
                      setVistaCredentials(credentials);
                      setVistaModalOpen(true);
                    } catch (error) {
                      console.error('Failed to create Vista connection:', error);
                      enqueueSnackbar(
                        t('erpConnection.vistaConnectionFailed', 'Failed to create Vista connection'),
                        { variant: 'error' }
                      );
                    } finally {
                      setConnecting(false);
                    }
                    return;
                  }
                  if (value === AgaveSourceSystem.SAGE_INTACCT) {
                    setConnecting(true);
                    try {
                      // SEE-1062: 'onboarding' routes the post-OAuth callback back
                      // to the stepper so the entity picker surfaces here.
                      const authUrl = await getIntacctAuthUrl(selectedPaidol.id, 'onboarding');
                      window.location.href = authUrl;
                    } catch (error) {
                      console.error('Failed to get Intacct auth URL:', error);
                      enqueueSnackbar(
                        t('erpConnection.intacctConnectionFailed', 'Failed to connect to Sage Intacct'),
                        { variant: 'error' }
                      );
                      setConnecting(false);
                    }
                    return;
                  }
                  // SEE-1050: Sage 300 CRE connects via hh2 credentials (modal).
                  if (value === AgaveSourceSystem.SAGE_300_CRE) {
                    setSage300ModalOpen(true);
                    return;
                  }
                  // SEE-1175: Acumatica connects via a per-tenant credential modal
                  // then an OAuth redirect.
                  if (value === AgaveSourceSystem.ACUMATICA) {
                    setAcumaticaModalOpen(true);
                    return;
                  }
                  if (value === AgaveSourceSystem.QUICKBOOKS_DESKTOP && qbdFlagEnabled) {
                    setQbdModalOpen(true);
                    return;
                  }
                  setShowSetupMessage(true);
                }}
              >
                {erpV1Connected ? t('connected') : t('connect')}
              </ConnectButton>
            )}
          </Box>
        </Grid>
        {spectrumCredentials && (
          <SpectrumConnectionModal
            open={spectrumModalOpen}
            apiKey={spectrumCredentials.apiKey}
            endpointUrl={spectrumCredentials.endpointUrl}
            onClose={() => {
              setSpectrumModalOpen(false);
              setSpectrumCredentials(null);
              window.location.reload();
            }}
          />
        )}
        {vistaCredentials && (
          <VistaConnectionModal
            open={vistaModalOpen}
            apiKey={vistaCredentials.apiKey}
            endpointUrl={vistaCredentials.endpointUrl}
            onClose={() => {
              setVistaModalOpen(false);
              setVistaCredentials(null);
              window.location.reload();
            }}
          />
        )}
        {/* Sage 300 (hh2) credential-entry modal — SEE-1050 */}
        {selectedPaidol?.id && (
          <Sage300ConnectModal
            open={sage300ModalOpen}
            paidolId={selectedPaidol.id}
            onSuccess={() => {
              setSage300ModalOpen(false);
              enqueueSnackbar(t('erpConnection.sage300Connected', 'Sage 300 connected'), {
                variant: 'success',
              });
              window.location.reload();
            }}
            onClose={() => setSage300ModalOpen(false)}
          />
        )}
        {/* Acumatica per-tenant credential modal — SEE-1175. redirectOrigin
            'onboarding' routes the post-OAuth callback back to the stepper. The
            modal itself redirects the browser on submit; onSuccess just closes it. */}
        {selectedPaidol?.id && (
          <AcumaticaConnectModal
            open={acumaticaModalOpen}
            paidolId={selectedPaidol.id}
            redirectOrigin="onboarding"
            onSuccess={() => setAcumaticaModalOpen(false)}
            onClose={() => setAcumaticaModalOpen(false)}
          />
        )}
        {qbdFlagEnabled && selectedPaidol?.id && (
          <QBDConnectionModal
            open={qbdModalOpen}
            paidolId={selectedPaidol.id}
            orgName={selectedPaidol.name}
            onClose={finishQbd}
            onConnected={finishQbd}
          />
        )}
      </Grid>
    );
  }

  // Agave / Procore paidols get the existing flow
  return (
    <Grid
      container
      justifyContent="center"
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        pt: 3,
      }}
    >
      <Grid item xs={12}>
        <ContainerComponent
          title={t('connectAccountingSystem.title')}
          description={t('connectAccountingSystem.description')}
        />
      </Grid>
      <Grid
        item
        xs={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <ConnectToYourAccountingSystem value={value} setValue={setValue} disabled={isConnected} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            gap: '4rem',
            flexWrap: 'wrap',
            paddingTop: 3,
          }}
        >
          <Button onClick={handleNext} size="small">
            {t('illDoThisLater')}
          </Button>
          <ConnectButton
            loading={isLoading}
            disabled={isConnected || !value}
            {...(value === 'procore' ? { href: procoreUrl } : { onClick: openAgavePopup })}
          >
            {isConnected ? t('connected') : t('connect')}
          </ConnectButton>
        </Box>
      </Grid>
    </Grid>
  );
}

export default ConnectToAccountingSystemForm;
